import {
  Box,
  CircularProgress,
  IconButton,
  Typography,
  useMediaQuery,
  Avatar,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import MenuIcon from "@mui/icons-material/Menu";
import ShareIcon from "@mui/icons-material/Share";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import ModalBasicLayout from "../../Modal/ModalBasicLayout";
import Button from "../../Buttons/Button";
import AccordionSection from "../../Containers/Accordion";
import {
  BUTTON_CHANGE_CONTENT,
  BUTTON_SELECT_CONTENT,
  BUTTON_SHARE_LOADING,
  BUTTON_SHARE_MEDIA,
  BUTTON_SCHEDULE_MEDIA,
  BUTTON_SCHEDULING_MEDIA,
  GENERAL_INFORMATION,
  MODAL_SHARE_TITLE,
  MODAL_SHARE_SELECT_PLATFORMS_TITLE,
  PLATFORM_ONLY_ACCEPTS_VIDEOS,
  CAROUSEL_MIN_IMAGES_WARNING,
} from "../../../i18n/keysTranslations";
import ResponsiveConfigContainer from "./components/ResponsiveConfigContainer";
import GeneralSettingsForm from "./components/GeneralSettingsForm";
import PlatformSelector from "./components/PlatformSelector";
import SingleImagePreview from "./components/SingleImagePreview";
import CarouselGridPreview from "./components/CarouselGridPreview";
import { useShareModalMultiple } from "./hooks/useShareModalMultiple";
import InstagramAspectRatioModal from "./InstagramAspectRatioModal";
import { platformRegistry } from "./sharePlatforms";
import { PLATFORM_FORM_COMPONENTS } from "./utils/platforms";
import { logoPlatform } from "../../../utils/string";
import {
  INSTAGRAM as INSTAGRAM_CONSTANT,
  FACEBOOK as FACEBOOK_CONSTANT,
  LINKEDIN as LINKEDIN_CONSTANT,
  TIKTOK as TIKTOK_CONSTANT,
  MIN_CAROUSEL_IMAGES,
  ADVERTISERS_DEMO,
} from "../../../utils/constants";
import { useDispatchApp, useSelectorApp } from "../../../lib/redux";
import { getRoleCapability } from "../../../actions/getters";

const AccordionTitleWithIcon = ({
  iconSrc,
  iconAlt = "",
  text,
  iconSize = 22,
  textFontSize = 16,
  textFontWeight = 600,
  gap = 1.2,
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: gap,
      }}
    >
      <Avatar
        src={iconSrc}
        alt={iconAlt}
        variant="square"
        sx={{
          width: iconSize,
          height: iconSize,
          bgcolor: "transparent",
          ".MuiAvatar-img": {
            objectFit: "contain",
          },
        }}
      />
      <Typography
        variant="body2"
        sx={{
          fontWeight: textFontWeight,
          fontSize: textFontSize,
          lineHeight: 2,
        }}
      >
        {text}
      </Typography>
    </Box>
  );
};

const ShareModalMultiple = ({
  item,
  modalOpen,
  onCloseModal,
  scheduledDate = null,
  onRefreshCalendar,
  onScheduledShareSuccess,
  onShareSuccess,
  isEditing = false,
  editingData = null,
}) => {
  const dispatch = useDispatchApp();
  const canPost = dispatch(getRoleCapability("canPost"));
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const advertiserID = useSelectorApp(
    (state) => state.auth?.user?.advertiserID
  );
  const isDemoAdvertiser = ADVERTISERS_DEMO.includes(advertiserID);
  const isDemoContent = editingData?.originalData?.isDemoContent === true;

  const {
    methods,
    state,
    platformOptions,
    selectedPlatforms,
    handlers,
    formMeta,
  } = useShareModalMultiple({
    item,
    modalOpen,
    scheduledDate,
    onCloseModal,
    onRefreshCalendar,
    onScheduledShareSuccess,
    onShareSuccess,
    t,
    isEditing,
    editingData,
  });

  const { onChangeLinkedInAccount } = handlers;

  const {
    currentItems,
    currentItem,
    platformMediaItem,
    isCarouselMode,
    isConfigOpen,
    isImage,
    isVideo,
    isUploading,
    isGeneratingDescription,
    generalLanguage,
    generatorPlatformKey,
    invalidVideoPlatformLabel,
    isDescriptionRequired,
    maxCarouselImages,
    scheduledDateValue,
    suggestedScheduleReason,
    isLoadingSuggestedSchedule,
    uniqueInstagramBusinessList,
    uniqueFacebookPagesList,
    currentCarouselIndex,
    setCurrentCarouselIndex,
    isAspectRatioModalOpen,
  } = state;
  const {
    handleShare,
    toggleConfig,
    onTogglePlatform,
    onHandleKeyToggle,
    onGenerateDescription,
    openGallery,
    onReorderCarouselImages,
    onRemoveCarouselImage,
    onContentTypeChange,
    onAddCarouselImage,
    onAspectRatioGoBack,
    onAspectRatioContinueWithoutChanges,
    onAspectRatioResizeAndContinue,
  } = handlers;
  const { isSubmitDisabled, errors } = formMeta;
  const { control } = methods;

  const videoPlatformWarningLabel = invalidVideoPlatformLabel;

  if (!canPost) {
    return null;
  }

  return (
    <>
      <ModalBasicLayout
        modalOpen={modalOpen}
        onCloseModal={onCloseModal}
        titleContent={false}
        maxWidth={false}
        fullWidth
        fullScreen
        hasTitle={false}
        showCloseButton={false}
        sxContent={{
          py: 0,
          px: 0,
        }}
      >
        <IconButton
          aria-label="close"
          onClick={onCloseModal}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            zIndex: 1000,
            backgroundColor: "background.paperSecondary",
            color: "background.paperSecondaryContrastText",
            "&:hover": {
              backgroundColor: "background.paperSecondary",
              filter: "brightness(0.9)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        <FormProvider {...methods}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              height: "100vh",
              maxHeight: "100vh",
              overflow: "hidden",
            }}
          >
            <ResponsiveConfigContainer
              isSmallScreen={isSmallScreen}
              open={isConfigOpen}
              onClose={toggleConfig}
            >
              <Box
                component="form"
                onSubmit={handleShare}
                sx={{
                  width: { xs: "100%", sm: 350, md: 500 },
                  maxWidth: { xs: "100%", sm: 350, md: 500 },
                  bgcolor: "background.paper",
                  borderRight: { xs: 0, sm: "1px solid rgba(0, 0, 0, 0.12)" },
                  px: { xs: 2, sm: 3 },
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  position: "relative",
                  height: "100%",
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    position: "relative",
                    pt: 3,
                  }}
                >
                  <Box
                    sx={{
                      p: 1.5,
                      bgcolor: "primary.main",
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <ShareIcon
                      sx={{ color: "#ffffff !important", fontSize: 20 }}
                    />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        color: "text.primary",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontSize: 24,
                      }}
                    >
                      {t(MODAL_SHARE_TITLE)}
                    </Typography>
                  </Box>
                  {isSmallScreen && (
                    <IconButton
                      onClick={toggleConfig}
                      size="small"
                      sx={{
                        "&:hover": {
                          bgcolor: "transparent",
                        },
                      }}
                      aria-label="Cerrar configuración"
                    >
                      <CloseIcon />
                    </IconButton>
                  )}
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography variant="subtitle2" sx={{}}>
                    {t(MODAL_SHARE_SELECT_PLATFORMS_TITLE)}
                  </Typography>
                  <PlatformSelector
                    platformOptions={platformOptions}
                    selectedPlatforms={selectedPlatforms}
                    onTogglePlatform={onTogglePlatform}
                    onHandleKeyToggle={onHandleKeyToggle}
                  />
                </Box>

                {/* General Information */}
                <AccordionSection
                  title={t(GENERAL_INFORMATION)}
                  initialValue
                  sxTitle={{
                    fontSize: 16,
                    fontWeight: 600,
                  }}
                  sx={{
                    py: 1,
                    "&.Mui-expanded": {
                      margin: "0px !important",
                    },
                  }}
                >
                  <GeneralSettingsForm
                    control={control}
                    errors={errors}
                    currentItem={currentItem}
                    generalLanguage={generalLanguage}
                    isGeneratingDescription={isGeneratingDescription}
                    onGenerateDescription={onGenerateDescription}
                    onOpenGallery={
                      isCarouselMode ? onAddCarouselImage : () => openGallery()
                    }
                    generatorPlatformKey={generatorPlatformKey}
                    showDescriptionField={isDescriptionRequired}
                    suggestedScheduleReason={suggestedScheduleReason}
                    isLoadingSuggestedSchedule={isLoadingSuggestedSchedule}
                    t={t}
                  />
                </AccordionSection>

                {selectedPlatforms.map((platform) => {
                  const PlatformComponent = PLATFORM_FORM_COMPONENTS[platform];
                  if (!PlatformComponent) return null;
                  const platformLabel = t(
                    platformRegistry[platform]?.labelKey || platform
                  );

                  const platformLogo = logoPlatform(platform);

                  const extraProps =
                    platform === INSTAGRAM_CONSTANT
                      ? {
                          currentItem: platformMediaItem,
                          uniqueInstagramBusinessList,
                          isCarouselMode,
                          currentItems,
                          onContentTypeChange: onContentTypeChange,
                        }
                      : platform === FACEBOOK_CONSTANT
                        ? {
                            currentItem: platformMediaItem,
                            uniqueFacebookPagesList,
                            isCarouselMode,
                            currentItems,
                            onContentTypeChange: onContentTypeChange,
                          }
                        : platform === TIKTOK_CONSTANT
                          ? {
                              currentItem: platformMediaItem,
                              isCarouselMode,
                              currentItems,
                              onContentTypeChange: onContentTypeChange,
                            }
                          : platform === LINKEDIN_CONSTANT
                            ? {
                                onChangeAccount: onChangeLinkedInAccount,
                              }
                            : {};

                  return (
                    <AccordionSection
                      key={`${platform}-platform`}
                      title={
                        <AccordionTitleWithIcon
                          iconSrc={platformLogo}
                          iconAlt={platform}
                          text={platformLabel}
                        />
                      }
                      initialValue
                      sx={{
                        py: 1,
                        "&.Mui-expanded": {
                          margin: "0px !important",
                        },
                      }}
                    >
                      <Box sx={{ mt: 1 }}>
                        <PlatformComponent
                          control={control}
                          errors={errors}
                          t={t}
                          {...extraProps}
                        />
                      </Box>
                    </AccordionSection>
                  );
                })}

                <Box
                  sx={{
                    position: "sticky",
                    bottom: 0,
                    bgcolor: "background.paper",
                    pt: 2,
                    pb: { xs: 2, sm: 3 },
                    mt: "auto",
                    zIndex: 1000,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  {videoPlatformWarningLabel && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ fontWeight: 400, textAlign: "center" }}
                    >
                      {t(PLATFORM_ONLY_ACCEPTS_VIDEOS, {
                        platform: videoPlatformWarningLabel,
                      })}
                    </Typography>
                  )}

                  {isCarouselMode &&
                    currentItems.length < MIN_CAROUSEL_IMAGES && (
                      <Typography
                        variant="caption"
                        color="warning.main"
                        sx={{ fontWeight: 400, textAlign: "center" }}
                      >
                        {t(CAROUSEL_MIN_IMAGES_WARNING)}
                      </Typography>
                    )}

                  {!isDemoContent && (
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      fullWidth
                      disabled={isSubmitDisabled || isDemoAdvertiser}
                      sx={{
                        py: 1.5,
                        fontWeight: 600,
                        textTransform: "none",
                        position: "relative",
                      }}
                    >
                      {isUploading ? (
                        <>
                          <CircularProgress
                            size={20}
                            color="inherit"
                            sx={{ mr: 1 }}
                          />
                          {t(
                            scheduledDateValue
                              ? BUTTON_SCHEDULING_MEDIA
                              : BUTTON_SHARE_LOADING
                          )}
                        </>
                      ) : (
                        t(
                          scheduledDateValue
                            ? BUTTON_SCHEDULE_MEDIA
                            : BUTTON_SHARE_MEDIA
                        )
                      )}
                    </Button>
                  )}
                </Box>
              </Box>
            </ResponsiveConfigContainer>

            <Box
              sx={{
                flex: 1,
                p: { xs: 2, sm: 4 },
                pt: { xs: 6, sm: 6 },
                pb: "0px !important",
                overflowY: "auto",
                position: "relative",
                height: "100vh",
                maxHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                backgroundColor: "background.paper",
              }}
            >
              {!isConfigOpen && isSmallScreen && (
                <IconButton
                  onClick={toggleConfig}
                  sx={{
                    position: "absolute",
                    top: 15,
                    left: 10,
                    zIndex: 10,
                  }}
                >
                  <MenuIcon />
                </IconButton>
              )}
              <Box
                sx={{
                  flex: 1,
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 2,
                  minHeight: 0,
                }}
              >
                {isCarouselMode ? (
                  <CarouselGridPreview
                    images={currentItems}
                    onReorder={onReorderCarouselImages}
                    onRemove={onRemoveCarouselImage}
                    onAddImage={onAddCarouselImage}
                    currentIndex={currentCarouselIndex}
                    onIndexChange={setCurrentCarouselIndex}
                    maxImages={maxCarouselImages}
                    t={t}
                  />
                ) : (
                  <SingleImagePreview
                    item={currentItems[0]}
                    isImage={isImage}
                    isVideo={isVideo}
                    t={t}
                  />
                )}
              </Box>
              <Box
                sx={{
                  pb: 3,
                  width: "100%",
                  textAlign: "center",
                  backgroundColor: "white",
                  flexShrink: 0,
                }}
              >
                <Button
                  color="secondary"
                  onClick={
                    isCarouselMode ? onAddCarouselImage : () => openGallery()
                  }
                  sx={{
                    textTransform: "none",
                  }}
                >
                  {currentItems.length > 0
                    ? t(BUTTON_CHANGE_CONTENT)
                    : t(BUTTON_SELECT_CONTENT)}
                </Button>
              </Box>
            </Box>
          </Box>
        </FormProvider>
      </ModalBasicLayout>

      <InstagramAspectRatioModal
        open={isAspectRatioModalOpen}
        onBack={onAspectRatioGoBack}
        onContinueWithoutChanges={onAspectRatioContinueWithoutChanges}
        onResizeAndContinue={onAspectRatioResizeAndContinue}
      />
    </>
  );
};

export default ShareModalMultiple;
